import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "@/Utils/firebase";
import { logInfo } from "@/Utils/logger";
import {
  clearAvatarSharingData,
  getSharedScanId,
} from "@/Utils/avatarSharingStorage";

/**
 * How long either read below may take before we stop waiting.
 *
 * Both run when the scan form opens, never on the boot/splash path — an
 * unbounded read there is exactly what BUG-0011 was. On expiry the answer is
 * the safe one: the share is KEPT (the form shows who it is going to, and
 * Cancel ends it), and a missing client name shows `shareRecipientLabel`'s
 * placeholder.
 */
export const SHARED_SCAN_READ_BUDGET_MS = 5_000;

export type SharedScanOutcome = "none" | "kept" | "cleared" | "unreadable";

const TIMED_OUT = Symbol("timed-out");

/**
 * Race a read against the budget. The timer is registered synchronously so
 * callers (and tests with fake timers) can rely on it being armed as soon as
 * the read starts. The losing read is left running — it is a read.
 */
function withinBudget<T>(read: Promise<T>): Promise<T | typeof TIMED_OUT> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), SHARED_SCAN_READ_BUDGET_MS);
  });
  return Promise.race<T | typeof TIMED_OUT>([read, expiry]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

/**
 * Settle a share that has already been used for a scan (BUG-0012).
 *
 * `ScanModel.create` no longer clears the share on submit; it records the scan
 * the share was used for. Here, when the next form opens, that scan decides:
 *
 * - APPROVED → the client has one good scan; the share ends, so a second scan
 *   in the same visit is not sent without the shopper starting a share again.
 * - anything else (FAILURE, still PROCESSING/REVIEW, doc missing) → the share
 *   stays, so a redo after a failed avatar reaches the same client.
 * - unreadable (error or budget spent) → the share stays: never silently drop
 *   a share on a guess — the silent drop is the bug being fixed.
 *
 * Measured when chosen: approval lands a median 2.0 min after submit (p90
 * 3.0, n = 353 prod scans), well before a second scan can finish, so in
 * practice this keeps the share only for redos.
 */
export async function resolveSharedScanShare(): Promise<SharedScanOutcome> {
  const sharedScanId = getSharedScanId();
  if (!sharedScanId) return "none";

  try {
    const snapshot = await withinBudget(getDoc(doc(db, "scans", sharedScanId)));
    if (snapshot === TIMED_OUT) {
      logInfo("[avatarSharing] shared scan status unreadable — keeping the share", {
        sharedScanId,
        reason: "timeout",
        budgetMs: SHARED_SCAN_READ_BUDGET_MS,
      });
      return "unreadable";
    }

    const pipelineStatus = snapshot.exists()
      ? (snapshot.data()?.pipelineStatus ?? null)
      : null;

    if (pipelineStatus === "APPROVED") {
      clearAvatarSharingData();
      logInfo("[avatarSharing] share cleared — the scan it was used for is approved", {
        sharedScanId,
      });
      return "cleared";
    }

    logInfo("[avatarSharing] share kept — the scan it was used for is not approved", {
      sharedScanId,
      pipelineStatus,
    });
    return "kept";
  } catch (error) {
    logInfo("[avatarSharing] shared scan status unreadable — keeping the share", {
      sharedScanId,
      reason: "error",
      errorMessage: (error as Error)?.message ?? String(error),
    });
    return "unreadable";
  }
}

/**
 * Look up a client's display name from its share code, the same way the
 * /send-avatar landing does (`App.tsx`): `orgName`, else the account holder's
 * `name`. Returns null when not found, on error, or when the budget runs out —
 * the caller then leaves `shareRecipientLabel`'s placeholder standing.
 *
 * Called from the scan form and from the checklist (`BeforeYouStartScreen`),
 * which are the two screens a timed-out /send-avatar landing reaches. Never
 * from the boot/splash path.
 */
export async function resolveShareOrgName(
  clientId: string,
): Promise<string | null> {
  try {
    const snapshot = await withinBudget(
      getDocs(
        query(collection(db, "clientIds"), where("clientId", "==", clientId)),
      ),
    );
    if (snapshot === TIMED_OUT || snapshot.empty) return null;
    const data = snapshot.docs[0].data();
    return data.orgName?.trim() || data.name?.trim() || null;
  } catch {
    return null;
  }
}
